package com.devi3east.bashbuddy.ticketDetail.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.dashboard.ui.DashboardUiState
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.purchase.domain.models.Allocation
import com.devi3east.bashbuddy.purchase.requests.UpdateAllocationsRequest
import com.devi3east.bashbuddy.ticketDetail.ui.components.AllocationCard
import com.devi3east.bashbuddy.ticketDetail.ui.components.EmptyTicketDetailContent

@Composable
fun TicketDetailRoute(
    appState: AppState,
    ticketId: String
) {
    val viewModel = appState.dashboardViewModel
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.loadAllocationsForTicketDetail(ticketId)
    }

    val allocations = uiState.allocationsPerTicket[ticketId]

    val ticket = uiState.ticketList.find { it._id == ticketId }
    val ticketTitle = ticket?.title ?: allocations?.firstOrNull()?.ticketTitle ?: "Ticket"
    val chancesPerTicket = ticket?.chancesPerTicket!!
    val serialNumber = ticket.serialNumber ?: ""

    TicketDetailScreen(
        appState = appState,
        uiState = uiState,
        ticketId = ticketId,
        ticketTitle = ticketTitle,
        serialNumber = serialNumber,
        allocations = allocations,
        chancesPerTicket = chancesPerTicket,
        maxChanceNumber = ticket.maxChanceNumber?: 0,
        onSubmitAssign = { request -> viewModel.submitAssignAndRefresh(request) },
        onBack = { appState.navController.popBackStack() }
    )
}

@Composable
fun TicketDetailScreen(
    appState: AppState,
    uiState: DashboardUiState,
    ticketId: String,
    ticketTitle: String,
    serialNumber: String,
    allocations: List<Allocation>?,
    chancesPerTicket: Int,
    maxChanceNumber: Int,
    onSubmitAssign: (UpdateAllocationsRequest) -> Unit,
    onBack: () -> Unit
) {
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
    ) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Box(modifier = Modifier.fillMaxSize()) {
            when {
                uiState.isTicketDetailLoading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }

                uiState.errorMessage != null -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = uiState.errorMessage,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }

                allocations.isNullOrEmpty() -> {
                    Box(
                        modifier = Modifier
                            .fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        EmptyTicketDetailContent(onBack = onBack)
                    }
                }

                else -> {
                    Column(
                        modifier = contentModifier.padding(horizontal = 16.dp)
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            IconButton(onClick = onBack) {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                    contentDescription = "Back",
                                    tint = MaterialTheme.colorScheme.onSurface
                                )
                            }

                            Spacer(Modifier.width(8.dp))

                            Column {
                                Text(
                                    text = ticketTitle,
                                    style = MaterialTheme.typography.titleLarge.copy(
                                        fontWeight = FontWeight.Bold
                                    ),
                                    color = MaterialTheme.colorScheme.onSurface
                                )

                                if (serialNumber.isNotBlank()) {
                                    Text(
                                        text = "Serial #: $serialNumber",
                                        style = MaterialTheme.typography.bodySmall.copy(
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    )
                                }

                                if (maxChanceNumber > 0) {
                                    val digits = maxChanceNumber.toString().length
                                    val startRange = "0".repeat(digits).take(digits)
                                    val endRange = maxChanceNumber.toString().padStart(digits, '0')

                                    Text(
                                        text = "Range: $startRange - $endRange",
                                        style = MaterialTheme.typography.bodySmall.copy(
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    )
                                }
                            }
                        }

                        Spacer(Modifier.height(16.dp))

                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            items(allocations) { allocation ->
                                val otherAssignedNumbers = allocations
                                    .filter { it.allocationId != allocation.allocationId }
                                    .flatMap { it.chanceNumbers }
                                    .filter { it.isNotBlank() }

                                AllocationCard(
                                    ticketId = ticketId,
                                    allocation = allocation,
                                    chancesPerTicket = chancesPerTicket,
                                    assignedNumbers = otherAssignedNumbers,
                                    maxChanceNumber = maxChanceNumber,
                                    onSubmitAssign = { ticketId, updatedAllocations ->
                                        val allocationMap = updatedAllocations.associate { it.allocationId to it.chanceNumbers }
                                        val request = UpdateAllocationsRequest(ticketId = ticketId, updates = allocationMap)
                                        onSubmitAssign(request)
                                    }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}